import { Copy } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { getErrorMessage } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { useDuplicateMyDesign } from '../model/use-duplicate-my-design';

const DUPLICATE_FALLBACK = '디자인을 복제하지 못했어요. 잠시 후 다시 시도해 주세요.';

interface DuplicateMyDesignButtonProps {
  readonly design: { readonly id: string; readonly name: string };
}

/** 카드 이름 줄의 복제 아이콘 — 누르면 바로 사본을 만들고 목록 맨 앞에 "이름 (사본)"이 생긴다. 실패 사유는 툴팁 대신 아이콘 아래 한 줄. */
export const DuplicateMyDesignButton = ({ design }: DuplicateMyDesignButtonProps): ReactElement => {
  const { duplicate, isPending } = useDuplicateMyDesign();
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setError(null);
    try {
      await duplicate(design.id);
    } catch (caught) {
      setError(getErrorMessage(caught, DUPLICATE_FALLBACK));
    }
  };

  return (
    <span className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`${design.name} 복제`}
        title="복제"
        className="size-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        isLoading={isPending}
        onClick={() => void run()}
      >
        <Copy aria-hidden className="size-4" strokeWidth={1.5} />
      </Button>
      {error ? (
        <span
          role="alert"
          className="absolute top-full right-0 z-10 mt-1 w-max max-w-56 bg-background px-2 py-1 text-xs text-destructive shadow-sm"
        >
          {error}
        </span>
      ) : null}
    </span>
  );
};
